/**
 * Configuration centralisée de la marque REGARD DE NATURE
 * Point unique de vérité pour toutes les informations de branding
 */

export const BRAND = {
  // Nom de marque
  name: {
    line1: "REGARD",
    line2: "DE NATURE",
    full: "REGARD DE NATURE",
  },
  
  // Baseline
  baseline: {
    full: "Naturopathie pour petits & grands",
    parts: {
      text1: "Naturopathie pour petits",
      separator: "&",
      text2: "grands",
    },
  },
  
  // Praticienne
  practitioner: {
    name: "Laura",
    title: "Naturopathe - Iridologue",
  },
  
  // Signature email
  emailSignature: {
    name: "Laura",
    brand: "REGARD DE NATURE",
    baseline: "Naturopathie pour petits & grands",
  },
  
  // Chemins des logos
  logos: {
    main: "/regard-de-nature-logo.png",
    mainImport: "@/assets/regard-de-nature-logo.png",
    email: "https://hukmgamisjjgulkqacxm.supabase.co/storage/v1/object/public/assets/regard-de-nature-logo.png",
  },
  
  // Charte graphique - Couleurs
  colors: {
    // Couleurs principales
    gold: "#CCA727",          // REGARD, &, accents premium, CTA
    forestGreen: "#506842",   // DE NATURE, textes principaux
    sageGreen: "#859378",     // Accents secondaires
    lightBackground: "#FDF9F3", // Fond clair
    softAccent: "#D5CFA9",    // Accent doux
    
    // Couleurs HSL pour CSS
    hsl: {
      gold: "45 68% 47%",
      forestGreen: "92 20% 33%",
      sageGreen: "95 14% 52%",
      lightBackground: "42 67% 97%",
      softAccent: "50 33% 75%",
    },
    
    // RGB pour jsPDF
    rgb: {
      gold: { r: 204, g: 167, b: 39 },
      forestGreen: { r: 80, g: 104, b: 66 },
      sageGreen: { r: 133, g: 147, b: 120 },
      lightBackground: { r: 253, g: 249, b: 243 },
      softAccent: { r: 213, g: 207, b: 169 },
      text: { r: 60, g: 60, b: 60 },
      textLight: { r: 120, g: 120, b: 120 },
      white: { r: 255, g: 255, b: 255 },
    },
  },
  
  // Typographies
  fonts: {
    heading: "Cinzel",       // Titres, branding, PDFs
    body: "Montserrat",      // Textes, UI, emails, formulaires
  },
  
  // Métadonnées
  meta: {
    title: "REGARD DE NATURE - Naturopathie pour petits & grands",
    description: "REGARD DE NATURE - Cabinet de naturopathie et iridologie. Accompagnement personnalisé vers le bien-être naturel.",
    themeColor: "#506842",
    keywords: "naturopathie, iridologie, bien-être, santé naturelle, Laura",
  },
  
  // PWA / Manifest
  pwa: {
    name: "REGARD DE NATURE",
    shortName: "REGARD DE NATURE",
    description: "Naturopathie pour petits & grands",
    backgroundColor: "#FDF9F3",
    themeColor: "#506842",
  },
  
  // Footer texte légal
  legal: {
    copyright: (year: number) => `© ${year} REGARD DE NATURE – Naturopathie pour petits & grands. Tous droits réservés.`,
    disclaimer: "La naturopathie ne se substitue pas à un traitement médical et ne pose pas de diagnostic.",
    confidential: "Document personnel et confidentiel.",
  },
} as const;

// Helper pour générer le nom complet avec baseline
export const getFullBrandName = () => `${BRAND.name.full} – ${BRAND.baseline.full}`;

// Helper pour la signature email HTML
export const getEmailSignatureHtml = () => `
  <p style="margin-top: 20px;">
    À très bientôt,<br>
    <strong style="color: ${BRAND.colors.forestGreen};">${BRAND.emailSignature.name}</strong><br>
    <span style="color: ${BRAND.colors.gold};">${BRAND.emailSignature.brand}</span><br>
    <span style="font-size: 12px; color: ${BRAND.colors.forestGreen};">${BRAND.emailSignature.baseline}</span>
  </p>
`;

// Helper pour le header email HTML
export const getEmailHeaderHtml = (logoUrl?: string) => `
  <div style="background: linear-gradient(135deg, ${BRAND.colors.forestGreen} 0%, ${BRAND.colors.sageGreen} 100%); color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
    <div style="display: flex; align-items: center; justify-content: center; gap: 15px; margin-bottom: 15px;">
      ${logoUrl ? `<img src="${logoUrl}" alt="${BRAND.name.full}" style="width: 60px; height: 60px;" />` : ''}
      <div style="text-align: left;">
        <p style="font-size: 24px; font-weight: bold; margin: 0; color: ${BRAND.colors.gold}; font-family: Cinzel, serif;">${BRAND.name.line1}</p>
        <p style="font-size: 24px; font-weight: bold; margin: 0; color: white; font-family: Cinzel, serif;">${BRAND.name.line2}</p>
      </div>
    </div>
    <p style="font-size: 12px; margin: 0; letter-spacing: 2px;">
      <span style="color: white;">${BRAND.baseline.parts.text1}</span>
      <span style="color: ${BRAND.colors.gold};"> ${BRAND.baseline.parts.separator} </span>
      <span style="color: white;">${BRAND.baseline.parts.text2}</span>
    </p>
  </div>
`;

// Helper pour le footer email HTML
export const getEmailFooterHtml = () => `
  <div style="text-align: center; padding: 20px; color: #666; font-size: 12px; background: #f8f8f8;">
    <p style="margin: 0; color: ${BRAND.colors.forestGreen}; font-weight: bold;">${BRAND.name.full}</p>
    <p style="margin: 5px 0;">${BRAND.baseline.full}</p>
    <p style="margin: 10px 0 0 0; font-size: 10px;">Cet email a été envoyé automatiquement, merci de ne pas y répondre.</p>
  </div>
`;
